import * as z from "zod";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "../../../providers/form";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import GenericButton from "widgets/controls/buttons/generic";
import ComboBox from "widgets/shared/inputs/combobox";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { accountSiteList } from "store/appSelectors";
import { useEffect, useState } from "react";
import MultiCombobox from "widgets/shared/inputs/multiselectCombobox";
import DateRangePickerField from "widgets/shared/inputs/dateRangePicker";
import { DeviceListReqBody } from "api/types/device";
import { deviceList } from "api/device";
import { setLoading } from "store/appSlice";
import {
  ReportConfiguration,
  REPORT_TYPE_SYSTEM_UPTIME,
  SYSTEM_UPTIME_DEFAULT_TIMEZONE,
} from "models/reports/configuration";
import useWhiteLabel from "whitelabel/useWhiteLabel";
import InputField from "widgets/shared/inputs/inputField";
import { usePermissions } from "hooks/usePermissions";

// Schema
const SetupSchema = z
  .object({
    reportType: z
      .number()
      .min(1, { message: i18next.t("zod.error.report.type") }),
    site: z.string().optional(),
    device: z.string().optional(),
    devices: z.array(z.string()).optional(),
    timezone: z.number().optional(),
    models: z.array(z.string()).optional(),
    deviceModel: z.string().optional(),
    dateRange: z
      .any()
      .transform((val) => (Array.isArray(val) ? val : [])) // normalize to array
      .refine(
        (val): val is [Date, Date] =>
          val.length === 2 && val[0] instanceof Date && val[1] instanceof Date,
        { message: i18next.t("zod.error.date.range.required") }
      )
      .refine(([start, end]) => start <= end, {
        message: i18next.t("zod.error.date.range"),
      }),
    email: z
      .union([
        z.literal(""),
        z.string().email({ message: i18next.t("zod.error.email") }),
      ])
      .optional(),
  })
  .refine(
    (data) => {
      if (data.reportType === REPORT_TYPE_SYSTEM_UPTIME) return true;
      return !!(data.site && data.site.length > 0);
    },
    { message: i18next.t("zod.error.site"), path: ["site"] }
  )
  .refine(
    (data) => {
      if (data.reportType === REPORT_TYPE_SYSTEM_UPTIME) return true;
      if (data.reportType === 2 || data.reportType === 3) {
        return data.devices && data.devices.length > 0;
      }
      if (data.reportType === 1) {
        return data.device && data.device.length > 0;
      }
      return true;
    },
    {
      message: i18next.t("zod.error.report.type"),
      path: ["device", "devices"],
    }
  )
  .refine(
    (data) => {
      if (data.reportType !== REPORT_TYPE_SYSTEM_UPTIME) return true;
      const dr = data.dateRange;
      return (
        Array.isArray(dr) &&
        dr.length === 2 &&
        dr[0] instanceof Date &&
        dr[1] instanceof Date &&
        dr[0] <= dr[1]
      );
    },
    {
      message: i18next.t("zod.error.date.range.required"),
      path: ["dateRange"],
    }
  )
  .superRefine((data, ctx) => {
    if (data.reportType !== REPORT_TYPE_SYSTEM_UPTIME) return;
    const e = (data.email ?? "").trim();
    if (!e) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18next.t("zod.error.required"),
        path: ["email"],
      });
      return;
    }
    if (!z.string().email().safeParse(e).success) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18next.t("zod.error.email"),
        path: ["email"],
      });
    }
  });

export type SetupFormType = z.infer<typeof SetupSchema>;

// Props
type SetupFormProps = {
  submit: (data: SetupFormType) => void;
};

const SetupForm = ({ submit }: SetupFormProps) => {
  const { availableReports } = useWhiteLabel();
  const { canShow } = usePermissions();
  const form = useForm<SetupFormType>({
    resolver: zodResolver(SetupSchema),
  });
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const siteList = useSelector(accountSiteList, shallowEqual);
  const [devList, setDevList] = useState([]);
  const [customError, setCustomError] = useState<string | null>(null);
  const [gLoading, setDevLoading] = useState(false);

  const { errors } = form.formState;

  const [selectedReportConfig, setSelectedReportConfig] = useState(null);

  // Filter ReportConfiguration based on availableReports and optional minRole (super-only, etc.)
  const filteredReportConfig = ReportConfiguration.filter(
    (r) =>
      availableReports?.includes(r.id) &&
      (!r.minRole || canShow(r.minRole))
  );

  const onSubmit = async (formData: SetupFormType) => {
    try {
      await submit(
        formData.reportType === REPORT_TYPE_SYSTEM_UPTIME
          ? { ...formData, timezone: SYSTEM_UPTIME_DEFAULT_TIMEZONE }
          : formData
      );
    } catch (error) {
      setCustomError(t("form.error.unknown"));
    }
  };

  const clearErrors = () => {
    form.clearErrors();
    setCustomError(null);
  };

  const fetchDeviceList = async (siteId: string) => {
    if (!siteId) return;
    clearErrors();
    form.setValue("device", "");
    form.setValue("devices", []);
    form.setValue("deviceModel", undefined);
    form.setValue("dateRange", null);

    setDevLoading(true);
    const body: DeviceListReqBody = {
      classId: siteId,
      sort: "snr",
      desc: true,
      installed: "INSTALLED",
      models: selectedReportConfig?.models,
    };
    dispatch(setLoading(true));
    const { data, isLoading } = await deviceList(body);
    dispatch(setLoading(isLoading));
    setDevLoading(isLoading);

    if (data) {
      setDevList(data);
      if (data.length === 0) setCustomError(t("zod.error.no.devices"));
    }
  };

  useEffect(() => {
    // Clear device selection when reportType changes
    form.setValue("device", "");
    form.setValue("devices", []);
    form.setValue("deviceModel", undefined);
    form.setValue("dateRange", null);
    form.setValue("site", "");
    form.setValue(
      "timezone",
      selectedReportConfig?.systemReport
        ? SYSTEM_UPTIME_DEFAULT_TIMEZONE
        : undefined
    );
  }, [selectedReportConfig]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="mt-6 space-y-6">
        <div className="grid grid-cols-1 gap-6">
          <FormField
            control={form.control}
            name="reportType"
            rules={{ required: true }}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-white">
                  {t("form.input.select.report.type")}
                </FormLabel>
                <FormControl>
                  <ComboBox
                    items={filteredReportConfig}
                    placeholder={t("form.placeholder.select.report.type")}
                    id="report-type"
                    labelKey="name"
                    valueKey="id"
                    state={errors.reportType ? "error" : undefined}
                    onFocus={clearErrors}
                    value={field.value}
                    onChange={(value) => {
                      field.onChange(value);
                      const config = filteredReportConfig.find(
                        (item) => item.id === value
                      );
                      setSelectedReportConfig(config);
                      form.setValue("models", config?.models || []);
                    }}
                    clearIcon={false}
                    sizes="md"
                    color="secondary"
                    variant="solid"
                  />
                </FormControl>
              </FormItem>
            )}
          />
          {!selectedReportConfig?.systemReport && (
            <FormField
              control={form.control}
              name="site"
              rules={{ required: true }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-white">
                    {t("form.input.select.site")}
                  </FormLabel>
                  <FormControl>
                    <ComboBox
                      items={siteList}
                      placeholder={t("form.placeholder.select.site")}
                      id="site"
                      labelKey="name"
                      valueKey="id"
                      value={field.value}
                      state={errors.site ? "error" : undefined}
                      onFocus={clearErrors}
                      onChange={(value) => {
                        field.onChange(value);
                        fetchDeviceList(value);
                      }}
                      filter
                      sizes="md"
                      color="secondary"
                      variant="solid"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          )}
          {(selectedReportConfig?.systemReport ||
            (form.watch("site") &&
              !gLoading &&
              selectedReportConfig?.dateRange)) && (
              <FormField
                control={form.control}
                name="dateRange"
                rules={{ required: true }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-white">
                      {t("form.input.select.date.range")}
                    </FormLabel>
                    <FormControl>
                      <DateRangePickerField
                        id="date-range"
                        onFocus={clearErrors}
                        value={field.value as [Date, Date]} // Ensure the value is cast to the correct type
                        onChange={(value) => {
                          field.onChange(value); // value should be of type LooseValue
                        }}
                        sizes="md"
                        color="secondary"
                        maxDate={new Date()}
                        variant="solid"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            )}
          {form.watch("site") &&
            !gLoading &&
            selectedReportConfig?.multiSelect &&
            devList.length > 0 && (
              <FormField
                control={form.control}
                name="devices"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-white">
                      {t("form.input.select.devices")}
                    </FormLabel>
                    <FormControl>
                      <MultiCombobox
                        items={devList}
                        placeholder={t("form.placeholder.select.devices")}
                        id="devices"
                        labelKey="name"
                        valueKey="snr"
                        value={field.value}
                        state={errors.devices ? "error" : undefined}
                        onFocus={clearErrors}
                        onChange={(value) => {
                          field.onChange(value);
                          // Auto-set timezone when first device is selected
                          if (value && value.length > 0) {
                            const firstSelectedDevice = devList.find(
                              (device) => device.snr === value[0]
                            );
                            if (
                              firstSelectedDevice &&
                              firstSelectedDevice.timezone !== undefined
                            ) {
                              form.setValue(
                                "timezone",
                                firstSelectedDevice.timezone
                              );
                            }
                            form.setValue(
                              "deviceModel",
                              firstSelectedDevice?.model
                            );
                          } else {
                            form.setValue("deviceModel", undefined);
                          }
                        }}
                        filter
                        selectAll
                        sizes="md"
                        color="secondary"
                        variant="solid"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            )}

          {form.watch("site") &&
            !gLoading &&
            selectedReportConfig?.singleSelect &&
            devList.length > 0 && (
              <FormField
                control={form.control}
                name="device"
                rules={{ required: true }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-white">
                      {t("form.input.select.device")}
                    </FormLabel>
                    <FormControl>
                      <ComboBox
                        items={devList}
                        placeholder={t("form.placeholder.select.device")}
                        id="device"
                        labelKey="name"
                        valueKey="snr"
                        value={field.value}
                        state={errors.device ? "error" : undefined}
                        onFocus={clearErrors}
                        onChange={(value) => {
                          field.onChange(value);
                          // Auto-set timezone when device is selected
                          if (value) {
                            const selectedDevice = devList.find(
                              (device) => device.snr === value
                            );
                            if (
                              selectedDevice &&
                              selectedDevice.timezone !== undefined
                            ) {
                              form.setValue(
                                "timezone",
                                selectedDevice.timezone
                              );
                            }
                            form.setValue(
                              "deviceModel",
                              selectedDevice?.model
                            );
                          } else {
                            form.setValue("deviceModel", undefined);
                          }
                        }}
                        sizes="md"
                        color="secondary"
                        filter
                        variant="solid"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            )}

          <FormField
            control={form.control}
            name="email"
            rules={{ required: false }}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-white">
                  {selectedReportConfig?.systemReport
                    ? t("form.input.email")
                    : t("form.input.mail")}
                </FormLabel>
                <FormControl>
                  <InputField
                    placeholder={t("form.placeholder.email")}
                    id="email"
                    type="text"
                    state={errors.email ? "error" : undefined}
                    onFocus={clearErrors}
                    sizes="md"
                    color="secondary"
                    variant="solid"
                    {...field}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          {(customError || Object.values(errors).length > 0) && (
            <p className={`mt-0 text-txt-md text-amber-300`}>
              {customError ||
                Object.values(errors)[0]?.message ||
                t("zod.error.form.fill")}
              *
            </p>
          )}
        </div>
        <div className="mt-12">
          <GenericButton
            type="submit"
            text={"Run"}
            extra="w-full"
            color="white"
          />
        </div>
      </form>
    </Form>
  );
};

export default SetupForm;
